import { DateCondition, getDateFromFilter } from '@fabric-ui/components';
import { SortedTimeBucketOptions, TimeBucket, TimeBucketSet } from '@fabric-ui/components/Table';
import Pluralize from 'pluralize';
import { DwQueryRequest, Field } from '../../../types';

export const INTUITIVE_LOOKBACK_THRESHOLDS = { recent: 90, extended: 180 } as const;
export const DEFAULT_DURATION_THRESHOLDS = { day: 14, week: 30 } as const;

export type TimeBucketValue = (typeof TimeBucket)[keyof typeof TimeBucket];
export type HistoricalLookbackBand = 'RECENT' | 'EXTENDED' | 'ARCHIVE';
export type NormalizedDateRange = { from: Date; to: Date };
export type HistoricalRangeInfo = { lookbackDays: number; durationDays: number; lookbackBand: HistoricalLookbackBand };
export type HistoricalBreakdownState = {
  allowedBreakdowns: TimeBucketValue[];
  disabledBreakdowns: TimeBucketValue[];
  selectedBreakdowns: TimeBucketValue[];
  defaultBreakdown: TimeBucketValue;
};

export const HISTORICAL_BREAKDOWN_OPTIONS = SortedTimeBucketOptions.filter(({ value }) =>
  [TimeBucket.BUCKET_DAY, TimeBucket.BUCKET_WEEK, TimeBucket.BUCKET_MONTH].includes(value as TimeBucketValue)
) as Array<{ label: string; value: TimeBucketValue }>;
export const ALL_HISTORICAL_BREAKDOWNS = HISTORICAL_BREAKDOWN_OPTIONS.map(({ value }) => value);

const toCalendarDay = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

export const normalizeHistoricalDateRange = (dateRange: DateCondition): NormalizedDateRange | undefined => {
  const { value } = getDateFromFilter(dateRange);
  const from = new Date(value.from);
  const to = new Date(value.to);
  return isValidDate(from) && isValidDate(to) ? { from, to } : undefined;
};

export const getHistoricalRangeInfo = (dateRange: DateCondition | NormalizedDateRange, now = new Date()): HistoricalRangeInfo => {
  const normalizedRange = 'from' in dateRange ? dateRange : normalizeHistoricalDateRange(dateRange);
  if (!normalizedRange || !isValidDate(normalizedRange.from) || !isValidDate(normalizedRange.to)) {
    return { lookbackDays: 0, durationDays: 0, lookbackBand: 'RECENT' };
  }
  const { from, to } = normalizedRange;
  const startDay = Math.min(toCalendarDay(from), toCalendarDay(to));
  const endDay = Math.max(toCalendarDay(from), toCalendarDay(to));
  const lookbackDays = Math.max(0, toCalendarDay(now) - startDay);
  const durationDays = endDay - startDay;
  const lookbackBand =
    lookbackDays < INTUITIVE_LOOKBACK_THRESHOLDS.recent
      ? 'RECENT'
      : lookbackDays < INTUITIVE_LOOKBACK_THRESHOLDS.extended
        ? 'EXTENDED'
        : 'ARCHIVE';
  return { lookbackDays, durationDays, lookbackBand };
};

export const getAllowedBreakdowns = (lookbackDays: number): TimeBucketValue[] => {
  if (lookbackDays < INTUITIVE_LOOKBACK_THRESHOLDS.recent) {
    return [...ALL_HISTORICAL_BREAKDOWNS];
  }
  if (lookbackDays < INTUITIVE_LOOKBACK_THRESHOLDS.extended) {
    return [TimeBucket.BUCKET_WEEK, TimeBucket.BUCKET_MONTH];
  }
  return [TimeBucket.BUCKET_MONTH];
};

export const getDefaultBreakdown = ({ lookbackDays, durationDays }: HistoricalRangeInfo): TimeBucketValue => {
  if (lookbackDays < INTUITIVE_LOOKBACK_THRESHOLDS.recent) {
    return durationDays <= DEFAULT_DURATION_THRESHOLDS.day ? TimeBucket.BUCKET_DAY : TimeBucket.BUCKET_WEEK;
  }
  if (lookbackDays < INTUITIVE_LOOKBACK_THRESHOLDS.extended) {
    return durationDays <= DEFAULT_DURATION_THRESHOLDS.week ? TimeBucket.BUCKET_WEEK : TimeBucket.BUCKET_MONTH;
  }
  return TimeBucket.BUCKET_MONTH;
};

export const reconcileSelectedBreakdowns = (selectedBreakdowns: TimeBucketValue[], rangeInfo: HistoricalRangeInfo): TimeBucketValue[] => {
  const allowedBreakdowns = getAllowedBreakdowns(rangeInfo.lookbackDays);
  const remainingSelections = selectedBreakdowns.filter(breakdown => allowedBreakdowns.includes(breakdown));
  return remainingSelections.length ? remainingSelections : [getDefaultBreakdown(rangeInfo)];
};

export const getHistoricalBreakdownState = (
  dateRange: DateCondition | NormalizedDateRange,
  selectedBreakdowns: TimeBucketValue[],
  now = new Date()
): HistoricalBreakdownState => {
  const rangeInfo = getHistoricalRangeInfo(dateRange, now);
  const allowedBreakdowns = getAllowedBreakdowns(rangeInfo.lookbackDays);
  return {
    allowedBreakdowns,
    disabledBreakdowns: ALL_HISTORICAL_BREAKDOWNS.filter(value => !allowedBreakdowns.includes(value)),
    selectedBreakdowns: reconcileSelectedBreakdowns(selectedBreakdowns, rangeInfo),
    defaultBreakdown: getDefaultBreakdown(rangeInfo),
  };
};

export const isBreakdownDisabled = (breakdown: string, allowedBreakdowns: TimeBucketValue[]) =>
  !allowedBreakdowns.includes(breakdown as TimeBucketValue);

export const reconcileRequestBreakdowns = <T extends DwQueryRequest>(request: T, dateRange: DateCondition): T => {
  const selectedFields = request.select.dims.filter(({ name }) => hasTimeBucket(name));
  if (!selectedFields.length) {
    return { ...request, timeRange: dateRange };
  }
  const selectedBreakdowns = selectedFields.map(({ name }) => name as TimeBucketValue);
  const selectedBreakdownSet = new Set(selectedBreakdowns);
  const nextBreakdowns = reconcileSelectedBreakdowns(selectedBreakdowns, getHistoricalRangeInfo(dateRange));
  const nextBreakdownSet = new Set(nextBreakdowns);
  const addedBreakdowns = nextBreakdowns.filter(breakdown => !selectedBreakdownSet.has(breakdown));
  const fieldsByName = new Map(selectedFields.map(field => [field.name, field]));

  const { fields: nextDims } = request.select.dims.reduce(
    (result, field) => {
      if (!hasTimeBucket(field.name) || nextBreakdownSet.has(field.name)) {
        return { ...result, fields: [...result.fields, field] };
      }
      if (result.insertedFallback) {
        return result;
      }
      return {
        fields: [...result.fields, ...addedBreakdowns.map(name => fieldsByName.get(name) ?? { name })],
        insertedFallback: true,
      };
    },
    { fields: [] as typeof request.select.dims, insertedFallback: false }
  );

  const { fields: nextGroupBy } = (request.groupBy ?? []).reduce(
    (result, name) => {
      if (!hasTimeBucket(name) || nextBreakdownSet.has(name)) {
        return { ...result, fields: [...result.fields, name] };
      }
      if (result.insertedFallback) {
        return result;
      }
      return { fields: [...result.fields, ...addedBreakdowns], insertedFallback: true };
    },
    { fields: [] as string[], insertedFallback: false }
  );

  const firstTimeSortIndex = request.sorting.findIndex(({ name }) => hasTimeBucket(name));
  const currentSortedBreakdown = firstTimeSortIndex >= 0 ? request.sorting[firstTimeSortIndex].name : undefined;
  const nextSortedBreakdown =
    currentSortedBreakdown && hasTimeBucket(currentSortedBreakdown) && nextBreakdownSet.has(currentSortedBreakdown)
      ? currentSortedBreakdown
      : HISTORICAL_BREAKDOWN_OPTIONS.find(({ value }) => nextBreakdownSet.has(value))?.value;
  const sorting = request.sorting.filter(({ name }, index) => !hasTimeBucket(name) || index === firstTimeSortIndex);
  if (firstTimeSortIndex >= 0 && nextSortedBreakdown) {
    sorting[firstTimeSortIndex] = { ...sorting[firstTimeSortIndex], name: nextSortedBreakdown };
  }

  return {
    ...request,
    timeRange: dateRange,
    select: { ...request.select, dims: nextDims },
    groupBy: nextGroupBy,
    sorting,
  };
};

export const hasTimeBucket = (value: string): value is TimeBucketValue => TimeBucketSet.has(value);

export const getSelectedBreakdownsTitle = (dims: Array<{ name: string }>) =>
  dims
    .filter(({ name }) => hasTimeBucket(name))
    .map(({ name }) => HISTORICAL_BREAKDOWN_OPTIONS.find(option => option.value === name)?.label)
    .filter((label): label is string => Boolean(label))
    .map(label => Pluralize(label).toUpperCase())
    .join(', ');

export const filterTimeBucketFromDimensions = (dims: Field[]) => dims.filter(({ name }) => !hasTimeBucket(name));
